import { Divider, Flex } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { memo } from 'react';
import bemFactory from 'react-bem-factory';
import {
    DifferenceWarning,
    SummaryLineTotal,
    SummaryLineValues,
    SummarySection,
    WarningTooltip,
} from 'shared/components';

import { useRequestItemsSummary } from './NetSuiteRequestItemsSummary.hooks';
import { messages } from './NetSuiteRequestItemsSummary.messages';
import { NetSuiteRequestItemsSummaryProps } from './NetSuiteRequestItemsSummary.types';

const qa = bemFactory.qa('netsuite-request-form-footer');

export const NetSuiteRequestItemsSummary = memo<NetSuiteRequestItemsSummaryProps>((props) => {
    const { netAmount, taxAmount, totalAmount, transactionDiscount, isInvoice = false } = props;

    const { ocrData, currency, currencyPrecision, showTotalWarning, showTaxTotalWarning, showSubtotalWarning } =
        useRequestItemsSummary(props);

    return (
        <SummarySection>
            <Flex size={12} justifyContent='end'>
                <Flex direction='column' spacing='4' width={420}>
                    <WarningTooltip
                        open={showSubtotalWarning}
                        activator={
                            <SummaryLineValues
                                warning={showSubtotalWarning}
                                label={isInvoice ? messages.invoiceNetAmountText : messages.netAmountText}
                                value1={intl.formatCurrency(netAmount, currency, currencyPrecision, false)}
                                qa={qa('net-amount')}
                            />
                        }
                    >
                        <DifferenceWarning
                            value1={netAmount}
                            value2={ocrData?.document?.subTotal}
                            label1={messages.differentAmountWarningLabel1}
                            label2={messages.differentAmountWarningLabel2}
                            currency={currency}
                            description={messages.differentAmountWarningDescription}
                        />
                    </WarningTooltip>

                    {isInvoice && (
                        <SummaryLineValues
                            label={messages.invoiceTransactionDiscountText}
                            value1={intl.formatCurrency(transactionDiscount, currency, currencyPrecision, false)}
                            qa={qa('transaction-discount')}
                        />
                    )}

                    <WarningTooltip
                        open={showTaxTotalWarning}
                        activator={
                            <SummaryLineValues
                                warning={showTaxTotalWarning}
                                label={isInvoice ? messages.invoiceTaxAmountText : messages.taxAmountText}
                                value1={intl.formatCurrency(taxAmount, currency, currencyPrecision, false)}
                                qa={qa('tax-amount')}
                            />
                        }
                    >
                        <DifferenceWarning
                            value1={taxAmount}
                            value2={ocrData?.document?.totalTax}
                            label1={messages.differentTaxTotalWarningLabel1}
                            label2={messages.differentTaxTotalWarningLabel2}
                            currency={currency}
                            description={messages.differentTaxTotalWarningDescription}
                        />
                    </WarningTooltip>

                    <Divider color='midnight80' spacing='8 0' />

                    <WarningTooltip
                        open={showTotalWarning}
                        activator={
                            <SummaryLineTotal
                                warning={showTotalWarning}
                                label={isInvoice ? messages.invoiceTotalAmountText : messages.totalAmountText}
                                value1={intl.formatCurrency(totalAmount, currency, currencyPrecision, false)}
                                qa={qa('total-amount')}
                            />
                        }
                    >
                        <DifferenceWarning
                            value1={totalAmount}
                            value2={ocrData?.document?.total}
                            label1={messages.differentTotalWarningLabel1}
                            label2={messages.differentTotalWarningLabel2}
                            currency={currency}
                            description={messages.differentTaxTotalWarningDescription}
                        />
                    </WarningTooltip>
                </Flex>
            </Flex>
        </SummarySection>
    );
});

NetSuiteRequestItemsSummary.displayName = 'NetSuiteRequestItemsSummary';
